import React from 'react'
import { Building2, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHotelTone } from '@/lib/hotel-palette'

interface HotelChipProps {
  name: string | null | undefined
  /**
   * Which hotel this is. Supplying it colour-bands the chip — every row of the same hotel wears the
   * same colour, on every page. Without it the chip falls back to matching on `name`, and without a
   * palette at all (ungrouped, or a unit test outside the layout) to the original two-tone look.
   */
  propertyId?: number | null
  /** The group's lead wears the crown — the hotel whose settings price every row on the page. */
  isLead?: boolean
  /**
   * `md` (default) is the chip as it has always been. `sm` rides along a row's own meta line
   * ("9 units · Max 2") and must sit *under* it in weight — it is an annotation on that line, not a
   * second thing to read.
   */
  size?: 'sm' | 'md'
  /**
   * `auto` (default) tints the lead teal and members slate. `accent` keeps the whole family in the
   * primary teal and lets the crown/building icon carry the lead distinction on its own — for lists
   * where the badge is a quiet annotation rather than the row's answer to "which of these two
   * identically-named rooms is it".
   */
  tone?: 'auto' | 'accent'
  className?: string
}

/**
 * WHOSE ROOM (or override, or logged change) THIS ROW IS.
 *
 * At a group, a unit-wide list mixes hotels, and the room name alone cannot say which one — two
 * hotels legitimately hold a room called "Double room-Standard". This is the calm answer: the
 * design system's minor badge (`bg-slate-100 text-slate-700`, `text-[11px]`), deliberately quieter
 * than the room name beside it. It exists to be scanned, not read.
 *
 * Renders nothing without a name, so a caller may mount it unconditionally.
 */
export default function HotelChip({
  name,
  propertyId,
  isLead = false,
  size = 'md',
  tone = 'auto',
  className,
}: HotelChipProps) {
  // Hooks run before the early return — a chip with no name still has to keep hook order stable.
  const toneFor = useHotelTone()
  const banded = toneFor(propertyId, name)

  if (!name) return null

  const Icon = isLead ? Crown : Building2

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full font-medium',
        size === 'sm'
          ? 'max-w-[170px] gap-0.5 px-1.5 py-px text-[10px]'
          : 'max-w-[190px] gap-1 px-1.5 py-0.5 text-[11px]',
        // The band is the hotel's identity and outranks both legacy tones. `accent` and `auto`
        // remain for ungrouped surfaces and for tests that render a chip outside the layout.
        banded
          ? banded.chip
          : tone === 'accent'
            ? 'bg-primary/10 text-primary'
            : isLead
              ? 'bg-primary/10 text-primary'
              : 'bg-slate-100 text-slate-700',
        className,
      )}
      title={isLead ? `${name}, the lead listing` : name}
    >
      <Icon className={size === 'sm' ? 'h-2 w-2 shrink-0' : 'h-2.5 w-2.5 shrink-0'} />
      <span className="truncate">{name}</span>
    </span>
  )
}
